"use client";

import { motion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { Play, CheckCircle2 } from "lucide-react";

import { DetectionCard } from "@/components/DetectionCard";
import { useDemoForm } from "@/lib/demo-form-context";

export function Hero() {
  const { open } = useDemoForm();
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -220]);
  const cardRotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

  return (
    <section ref={sectionRef} className="relative z-10 overflow-visible pt-12 pb-0 lg:pt-20">
      {/* Diagonal-clipped background — right side is 110px shorter, creating the slanted edge */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: "linear-gradient(135deg, #FDFDFD 0%, #F0F0F0 100%)",
          clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 110px), 0 100%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left Column: Copy */}
          <div className="max-w-2xl pt-4 pb-24 lg:pb-40">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-900/10 text-brand-900 text-sm font-medium mb-6 border border-brand-900/20"
            >
              <span className="w-2 h-2 rounded-full bg-brand-600 animate-pulse" />
              Computer Vision for Creator ROI
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-7xl font-display font-semibold tracking-tight text-gray-900 leading-[1.05] mb-6"
            >
              Pay creators for <br />
              <span className="text-brand-900">actual visibility.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-gray-600 mb-10 leading-relaxed max-w-xl"
            >
              Stop paying flat fees for sponsored posts. Our AI analyzes a creator&apos;s content frame-by-frame to measure exact brand exposure time, calculating payouts based on performance.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap items-center gap-4"
            >
              <button
                onClick={open}
                className="bg-brand-900 hover:bg-brand-800 text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-brand-900/20 hover:-translate-y-0.5 flex items-center gap-2 cursor-pointer"
              >
                <Play size={18} />
                See how it works
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="mt-12 flex items-center gap-6 text-sm text-gray-500 font-medium"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-brand-500" />
                Frame-by-frame analysis
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-brand-500" />
                Performance-based payouts
              </div>
            </motion.div>
          </div>

          {/* Right Column: Card — extends below the diagonal into teal section */}
          <motion.div
            style={{ y: cardY, rotate: cardRotate }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto w-full max-w-md lg:max-w-none pb-8 lg:pb-16"
          >
            <div className="absolute -inset-4 bg-gradient-to-tr from-brand-900/5 to-brand-400/5 rounded-[2.5rem] transform -rotate-3" />
            <div className="absolute -inset-4 bg-gradient-to-bl from-brand-900/5 to-brand-400/5 rounded-[2.5rem] transform rotate-2" />

            <DetectionCard />
          </motion.div>

        </div>
      </div>
    </section>
  );
}
